import { useState, useEffect } from "react";
import { BigNumber, Overrides, providers } from "ethers";
import { isPalmNetwork, BridgeConfig } from "../../chainbridgeConfig";
import { PalmBridge__factory as PalmBridgeFactory } from "../../Contracts/factories/PalmBridge__factory";
import { BridgeTollBooth__factory as TollBoothFactory } from "../../Contracts/factories/BridgeTollBooth__factory";
import { Erc20DetailedFactory } from "../../Contracts/Erc20DetailedFactory";
import type { BridgeTollBooth } from "../../Contracts/BridgeTollBooth";
import type { Erc20Detailed } from "../../Contracts/Erc20Detailed";
import { executeTransactionWithRetry } from "../chainbridgeHelpers";

interface UsePalmFeesArgs {
  signer: providers.JsonRpcSigner | undefined;
  homeChain: BridgeConfig | undefined;
  chainId: number;
}

export function usePalmFees({ signer, homeChain, chainId }: UsePalmFeesArgs) {
  const [homeChainIsPalm, setHomeChainIsPalm] = useState(false);
  const [palmRelayFee, setPalmRelayFee] = useState<BigNumber>(
    BigNumber.from("0")
  );
  const [palmGreenFee, setPalmGreenFee] = useState<BigNumber>(
    BigNumber.from("0")
  );
  const [totalPalmFees, setTotalPalmFees] = useState<BigNumber>(
    BigNumber.from("0")
  );
  const [tollBoothAddress, setTollBoothAddress] = useState<string>();
  const [tollBooth, setTollBooth] = useState<BridgeTollBooth>();
  const [tollTokenAddress, setTollTokenAddress] = useState<string>();
  const [tollToken, setTollToken] = useState<Erc20Detailed>();
  const [tollTokenSymbol, setTollTokenSymbol] = useState<string>();
  const [tollTokenDecimals, setTollTokenDecimals] = useState<number>();

  useEffect(() => {
    if (!homeChain || !chainId || !signer) return;

    const isPalm = isPalmNetwork(chainId);
    setHomeChainIsPalm(isPalm);

    if (!isPalm || homeChain.networkId !== chainId) return;

    const bridge = PalmBridgeFactory.connect(homeChain.bridgeAddress, signer);

    const fetchFeeData = async () => {
      const tollBoothAddr = await bridge.tollBooth();
      setTollBoothAddress(tollBoothAddr);

      const booth = TollBoothFactory.connect(tollBoothAddr, signer);
      setTollBooth(booth);

      const fees = await booth.getFees();
      setPalmRelayFee(fees.relayFee);
      setPalmGreenFee(fees.greenFee);

      const total = await booth.totalFees();
      setTotalPalmFees(total);

      const tollTokenAddr = await booth.tollToken();
      setTollTokenAddress(tollTokenAddr);

      const tollTokenContract = Erc20DetailedFactory.connect(
        tollTokenAddr,
        signer
      );
      setTollToken(tollTokenContract);
      setTollTokenSymbol(await tollTokenContract.symbol());
      setTollTokenDecimals(await tollTokenContract.decimals());
    };

    fetchFeeData();
  }, [homeChain, chainId, signer]);

  const allowPalmBridgeFees = async (gasPrice: number, gasLimit: number) => {
    if (!homeChainIsPalm)
      throw new Error(
        "allowBridgeFees() can only be run against the Palm bridge"
      );
    if (!tollToken)
      throw new Error(
        "cannot run allowBridgeFees(): tollToken not yet initialized"
      );
    if (!tollBoothAddress)
      throw new Error(
        "cannot run allowBridgeFees(): tollBoothAddress not yet initialized"
      );
    if (!tollBooth)
      throw new Error(
        "cannot run allowBridgeFees(): tollBooth not yet initialized"
      );

    const total = await tollBooth.totalFees();
    if (!total)
      throw new Error("cannot run allowBridgeFees(): totalFees not loaded");

    setTotalPalmFees(total);

    const tollApprovalTx = (options: Overrides) =>
      tollToken.approve(tollBoothAddress, total, options);
    await (
      await executeTransactionWithRetry({ gasPrice, gasLimit }, tollApprovalTx)
    ).wait(1);
  };

  return {
    homeChainIsPalm,
    palmRelayFee,
    palmGreenFee,
    totalPalmFees,
    tollBooth,
    tollTokenAddress,
    tollToken,
    tollTokenSymbol,
    tollTokenDecimals,
    allowPalmBridgeFees,
  };
}
